import React, { useEffect, useState } from 'react';
import api from '../utils/api';

interface OverviewUser {
  user_id: string;
  first_name: string;
  last_name: string;
  badge_number: string | null;
  station: string | null;
  completed: boolean;
  completed_at: string | null;
  notes: string | null;
}

interface OverviewData {
  year: number;
  total_users: number;
  completed: number;
  overdue: number;
  users: OverviewUser[];
}

export default function Inspections() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get<OverviewData>(`/inspections/overview?year=${year}`)
      .then(data => setOverview(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading...</div>;
  if (!overview) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Failed to load inspection data</div>;

  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 2; y--) yearOptions.push(y);

  // Filter users by search
  const filteredUsers = overview.users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${u.first_name} ${u.last_name}`.toLowerCase().includes(q);
  });

  const completedUsers = filteredUsers.filter(u => u.completed);
  const overdueUsers = filteredUsers.filter(u => !u.completed);
  const completionRate = overview.total_users > 0
    ? Math.round((overview.completed / overview.total_users) * 100)
    : 0;

  const thStyle = { padding: '10px 14px', textAlign: 'left' as const, fontWeight: 600, color: '#374151', fontSize: 13 };
  const tdStyle = { padding: '10px 14px', fontSize: 14 };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111' }}>Annual Inspections</h1>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: 'white' }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#16a34a' }}>{overview.completed}</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Completed</div>
        </div>
        <div style={{ background: 'white', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: overview.overdue > 0 ? '#f59e0b' : '#6b7280' }}>{overview.overdue}</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Outstanding</div>
        </div>
        <div style={{ background: 'white', borderRadius: 10, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: completionRate === 100 ? '#16a34a' : '#374151' }}>{completionRate}%</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Completion Rate</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name..."
          style={{ width: '100%', maxWidth: 400, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
      </div>

      {/* Outstanding / Not Completed */}
      <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', background: '#fffbeb' }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#92400e' }}>
            Outstanding ({overdueUsers.length})
          </h2>
        </div>
        {overdueUsers.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#16a34a', fontSize: 14, fontWeight: 500 }}>
            All personnel have completed their {year} inspection!
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Name</th>
              </tr>
            </thead>
            <tbody>
              {overdueUsers.map(u => (
                <tr key={u.user_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{u.first_name} {u.last_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Completed */}
      <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', background: '#f0fdf4' }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#166534' }}>
            Completed ({completedUsers.length})
          </h2>
        </div>
        {completedUsers.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
            No inspections completed yet for {year}.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Completed</th>
                <th style={thStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {completedUsers.map(u => (
                <tr key={u.user_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{u.first_name} {u.last_name}</td>
                  <td style={{ ...tdStyle, fontSize: 13, color: '#6b7280' }}>
                    {u.completed_at ? new Date(u.completed_at).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13, color: '#6b7280' }}>
                    {u.notes || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
